/**
 * 口コミ表示のWeb Component。Liquid非依存にしてあるので、
 * 将来アプリ化してApp Proxyに変わってもこのファイルは書き直さない。
 *
 *   <st-reviews handle="hare" limit="3" show-more></st-reviews>
 *   <st-review-rating handle="hare"></st-review-rating>
 *   <st-reviews filterable></st-reviews>            ← 口コミページ用（商品別に絞り込み＋追加読み込み）
 *
 * 絶対に守ること（決定事項 §3）:
 *   取得に失敗したら**枠ごと消す**。エラー文は出さない。購入導線は絶対に止めない。
 *
 * API は Worker（edgeキャッシュ越し）。承認時にパージするので即反映される。
 */
(() => {
  // 同じページの複数セクション（st-fv-reviews と st-seimei-voice 等）がこのファイルを
  // それぞれ <script src> で読むと、classicスクリプトは同一URLでも毎回実行され、
  // 2回目の customElements.define が例外を投げる（2026-08-14 本番コンソールで確認）。
  // 2回目以降の実行は何もしない。
  if (window.__ST_REVIEWS_LOADED__) return;
  window.__ST_REVIEWS_LOADED__ = true;

  const API = window.ST_REVIEWS_API || 'https://seitaitea-reviews.fortesnow.workers.dev';

  /**
   * 口コミページの1回あたりの表示件数。
   *
   * ★なぜページ番号送りにしないか（2026-07-31）★
   *   移行した口コミが674件ある。34ページのページャは押しにくい上に、
   *   戻ったときに表示件数とスクロール位置を両方失う。
   *   無限スクロールはフッターの特商法・プライバシーポリシーに
   *   到達できなくなるので通販では使えない。
   *   → 「もっと見る」で継ぎ足す。URLが変わらないので絞り込みとも共存する。
   */
  const PAGE = 20;

  /**
   * 一時的な失敗で枠が消えるのを防ぐ。
   * Cloudflare側の一過性エラー（1042など）を何度か観測しているので、
   * 「取れなかったら消す」の判定に入る前に必ず数回試す。
   */
  async function getJSON(url, tries = 3) {
    for (let i = 0; i < tries; i++) {
      try {
        const r = await fetch(url);
        if (r.ok) return await r.json();
      } catch { /* 次の試行へ */ }
      if (i < tries - 1) await new Promise((s) => setTimeout(s, 400 * (i + 1)));
    }
    return null;
  }

  // summary は1ページで何度も要るので1回だけ取って共有する
  let summaryPromise = null;
  const getSummary = () => {
    if (!summaryPromise) {
      summaryPromise = getJSON(`${API}/v1/summary`).then((j) => j?.summary || null);
    }
    return summaryPromise;
  };

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /** 4.92 → ★4つ＋半星。半星はグラデーションで作る（画像を足さない） */
  function stars(avg) {
    const pct = Math.max(0, Math.min(100, (avg / 5) * 100));
    return `<span class="st-rv__stars" role="img" aria-label="5点満点で${avg}点">
      <span class="st-rv__stars-bg">★★★★★</span>
      <span class="st-rv__stars-fg" style="width:${pct}%">★★★★★</span>
    </span>`;
  }

  class StReviewRating extends HTMLElement {
    async connectedCallback() {
      const handle = this.getAttribute('handle');
      const s = await getSummary();
      const d = handle ? s?.[handle] : null;
      if (!d || !d.count) { this.remove(); return; }   // 出せないなら消す
      const url = this.getAttribute('href') || `/pages/voice?product=${encodeURIComponent(handle)}`;
      this.innerHTML = `<a class="st-rv__rating" href="${esc(url)}">
        ${stars(d.avg)}<b>${d.avg.toFixed(2)}</b><span>（${d.count}件）</span></a>`;
    }
  }

  /**
   * 商品名から表示用の短い名前を作る。
   * 「整体茶 晴れ 28袋」→「晴れ」。タブに並べるので短くないと入らない。
   * 商品名が変わっても壊れないよう、取れなければ元の名前をそのまま返す。
   */
  function shortName(title) {
    return String(title || '')
      .replace(/^整体茶\s*/, '')
      .replace(/^整体屋の/, '')
      .replace(/\s*\d+\s*(袋|粒)\s*$/, '')
      .trim() || title;
  }

  /**
   * 総合評価＋商品別の切り替えタブ。トップページ用。
   *
   * ★以前は商品名のリンクを6つ縦に並べていた（2026-07-31に作り替え）★
   *   「整体茶 晴れ 28袋」のようにフルネームが6行続いてくどく、
   *   押せることも伝わりにくかった。zentena のセグメント型トグルに寄せ、
   *   短縮名のタブで総合と商品別を1枠の中で切り替える形にする。
   *
   * 合算の平均は件数で重み付けする。単純平均にすると13件の珈琲と
   * 392件の晴れが同じ重みになってしまう。
   */
  class StReviewsTotal extends HTMLElement {
    async connectedCallback() {
      const names = window.ST_REVIEWS_NAMES || {};
      const s = await getSummary();
      if (!s) { this.remove(); return; }
      const rows = Object.entries(s)
        .filter(([, d]) => d.count > 0)
        .sort((a, b) => b[1].count - a[1].count);
      if (!rows.length) { this.remove(); return; }

      // 総合を先頭に、以降は件数の多い順
      const total = {
        count: rows.reduce((a, [, d]) => a + d.count, 0),
        dist: [0, 0, 0, 0, 0],
      };
      rows.forEach(([, d]) => (d.dist || []).forEach((n, i) => { total.dist[i] += n || 0; }));
      total.avg = rows.reduce((a, [, d]) => a + d.avg * d.count, 0) / total.count;

      this.views = [{ h: '', label: '総合', ...total }]
        .concat(rows.map(([h, d]) => ({
          h, label: shortName(names[h] || h), avg: d.avg, count: d.count, dist: d.dist || [0, 0, 0, 0, 0],
        })));
      this.current = '';

      this.innerHTML = `
        <div class="st-rvt__tabs" role="tablist">${
          this.views.map((v) => `<button type="button" role="tab" class="st-rvt__tab" data-h="${esc(v.h)}"
            aria-selected="${v.h === '' ? 'true' : 'false'}">${esc(v.label)}</button>`).join('')
        }</div>
        <div class="st-rvt__panel"></div>`;

      this.panel = this.querySelector('.st-rvt__panel');
      this.querySelector('.st-rvt__tabs').addEventListener('click', (e) => {
        const b = e.target.closest('.st-rvt__tab');
        if (!b || b.dataset.h === this.current) return;
        this.current = b.dataset.h;
        this.querySelectorAll('.st-rvt__tab').forEach((x) =>
          x.setAttribute('aria-selected', String(x === b)));
        this.renderPanel();
        // 下の抜粋も同じ商品に合わせる。1画面で話が繋がるように
        document.dispatchEvent(new CustomEvent('st-reviews:filter', { detail: { handle: this.current } }));
      });

      this.renderPanel();
    }

    renderPanel() {
      const v = this.views.find((x) => x.h === this.current) || this.views[0];
      const bars = [5, 4, 3, 2, 1].map((n) => {
        const c = v.dist[n - 1] || 0;
        const pct = v.count ? (c / v.count) * 100 : 0;
        return `<li><span class="st-rvt__n">★${n}</span>
          <span class="st-rvt__bar"><i style="width:${pct.toFixed(1)}%"></i></span>
          <span class="st-rvt__v">${c}</span></li>`;
      }).join('');
      const more = v.h
        ? `<p class="st-rvt__more"><a href="/pages/voice?product=${encodeURIComponent(v.h)}">${
            esc(v.label)}の口コミを見る<span aria-hidden="true">›</span></a></p>`
        : '';
      this.panel.innerHTML = `
        <div class="st-rvt__head">
          <div class="st-rvt__score">
            <strong>${v.avg.toFixed(2)}</strong>
            ${stars(Math.round(v.avg * 100) / 100)}
            <span>${v.h ? '' : '全'}${v.count}件のお客様の声</span>
          </div>
          <ul class="st-rvt__dist">${bars}</ul>
        </div>${more}`;
    }
  }

  class StReviews extends HTMLElement {
    async connectedCallback() {
      const handle = this.getAttribute('handle') || '';
      const limit = +this.getAttribute('limit') || 3;
      const filterable = this.hasAttribute('filterable');
      const showMore = this.hasAttribute('show-more');

      // 口コミページは ?product= で初期の絞り込みを受ける
      const q = new URLSearchParams(location.search);
      this.current = handle || (filterable ? (q.get('product') || '') : '');
      this.filterable = filterable;
      // 口コミページは20件ずつ継ぎ足す。LP内の抜粋は指定件数を一度だけ
      this.per = filterable ? PAGE : limit;

      const ok = await this.load(0);
      if (!ok) { this.remove(); return; }              // 取れなければ枠ごと消す

      if (filterable) this.renderFilter();
      this.renderList(showMore, limit);

      // トップの切り替えタブに追随する（sync-filter を付けた要素だけ）。
      // タブで商品を選んだのに下の抜粋が総合のまま、では話が繋がらない。
      if (this.hasAttribute('sync-filter')) {
        document.addEventListener('st-reviews:filter', async (e) => {
          const h = (e.detail && e.detail.handle) || '';
          if (h === this.current) return;
          this.current = h;
          await this.load(0);
          this.list.innerHTML = this.rows.map(card).join('');
          this.updateMore();
        });
      }
    }

    /** いま選んでいる範囲の総件数。summary から出すので追加取得は要らない */
    total() {
      const s = this.sum || {};
      if (this.current) return s[this.current]?.count ?? this.rows.length;
      const all = Object.values(s).reduce((a, b) => a + (b.count || 0), 0);
      return all || this.rows.length;
    }

    /**
     * offset=0 で読み直し、それ以外は末尾に継ぎ足す。
     * 継ぎ足しでは this.rows を作り直さない（描画済みのカードを再生成しないため）。
     */
    async load(offset) {
      const u = new URL(`${API}/v1/reviews`);
      u.searchParams.set('limit', String(this.per));
      u.searchParams.set('offset', String(offset));
      if (this.current) u.searchParams.set('handle', this.current);
      const j = await getJSON(u.toString());
      if (!j) return false;
      const rows = j.reviews || [];
      this.rows = offset === 0 ? rows : this.rows.concat(rows);
      this.offset = this.rows.length;
      this.sum = await getSummary();
      // 継ぎ足しは0件でも「失敗」ではない。判定は初回だけ
      return offset === 0 ? this.rows.length > 0 : true;
    }

    renderFilter() {
      const names = window.ST_REVIEWS_NAMES || {};
      const handles = Object.keys(this.sum || {});
      if (handles.length < 2) return;
      const btn = (h, label) =>
        // 商品名が「…28袋」で終わるので、括弧を付けないと「28袋392」と繋がって読める
        `<button type="button" class="st-rv__fbtn${this.current === h ? ' is-on' : ''}"
                 data-h="${esc(h)}">${esc(label)}<small>${
          h ? `（${this.sum[h]?.count ?? 0}）` : ''}</small></button>`;
      const bar = document.createElement('div');
      bar.className = 'st-rv__filter';
      bar.innerHTML = btn('', 'すべて') + handles.map((h) => btn(h, names[h] || h)).join('');
      bar.addEventListener('click', async (e) => {
        const b = e.target.closest('.st-rv__fbtn');
        if (!b) return;
        this.current = b.dataset.h;
        bar.querySelectorAll('.st-rv__fbtn').forEach((x) => x.classList.toggle('is-on', x === b));
        await this.load(0);                            // 絞り込みを変えたら先頭20件から
        this.list.innerHTML = this.rows.map(card).join('');
        this.updatePager();
      });
      this.appendChild(bar);
    }

    renderList(showMore, limit) {
      this.list = document.createElement('div');
      this.list.className = 'st-rv__list';
      this.list.innerHTML = this.rows.map(card).join('');
      this.appendChild(this.list);

      if (this.filterable) this.renderPager();

      if (showMore) {
        this.moreEl = document.createElement('p');
        this.moreEl.className = 'st-rv__more';
        this.appendChild(this.moreEl);
        this.updateMore();
      }
      this.insertAdjacentHTML('beforeend',
        '<p class="st-rv__note">※掲載しているご感想はお客様個人の感想であり、商品の効果・効能を示すものではありません。</p>');
    }

    /** 「口コミをもっと見る」の飛び先。タブで商品が変わったら追随させる */
    updateMore() {
      if (!this.moreEl) return;
      const href = this.current
        ? `/pages/voice?product=${encodeURIComponent(this.current)}`
        : '/pages/voice';
      const n = this.current ? this.sum?.[this.current]?.count : null;
      this.moreEl.innerHTML = `<a href="${esc(href)}">口コミをもっと見る${
        n ? `<small>（全${n}件）</small>` : ''}<span aria-hidden="true">›</span></a>`;
    }

    /** 「もっと見る」。1度だけ作って中身だけ差し替える */
    renderPager() {
      this.pager = document.createElement('div');
      this.pager.className = 'st-rv__pager';
      this.pager.addEventListener('click', async (e) => {
        const b = e.target.closest('[data-more]');
        if (!b || this.busy) return;
        this.busy = true;
        b.disabled = true;
        b.textContent = '読み込み中…';
        const from = this.rows.length;
        await this.load(this.offset);
        const added = this.rows.slice(from);
        this.list.insertAdjacentHTML('beforeend', added.map(card).join(''));
        this.busy = false;
        this.updatePager();
        // 追加の先頭にフォーカスを移す。押した位置に留まると増えたことが伝わらない
        const first = this.list.children[from];
        if (first) {
          first.setAttribute('tabindex', '-1');
          first.focus({ preventScroll: true });
        }
      });
      this.appendChild(this.pager);
      this.updatePager();
    }

    updatePager() {
      if (!this.pager) return;
      const t = this.total();
      const shown = this.rows.length;
      const rest = Math.max(0, t - shown);
      this.pager.innerHTML = rest > 0
        ? `<p class="st-rv__count">全${t}件中 ${shown}件を表示</p>
           <button type="button" class="st-rv__more-btn" data-more>
             もっと見る<small>残り${rest}件</small></button>`
        : `<p class="st-rv__count">全${t}件をすべて表示しました</p>`;
    }
  }

  /* r.h は商品ハンドル。APIが最初から返しているのでそのまま画像に使える。
     2026-08-12 クライアント要望「ストーリーでシェアする時に商品写真があった方が良い」。

     写真はカード上部の帯に商品名と並べて置く。本文の左に縦置きすると
     スマホで本文幅が7〜8文字分削られ、行が刻まれて読みにくくなるため。
     画像が無い商品のときは帯ごと出さない（従来どおり文字だけのカード）。 */
  function card(r) {
    const imgs = window.ST_REVIEWS_IMAGES || {};
    const names = window.ST_REVIEWS_NAMES || {};
    const src = r.h ? imgs[r.h] : '';
    const name = r.h ? (names[r.h] || '') : '';
    /* 帯ごと1つのリンクにする。写真と商品名を別々のaにすると、
       商品名のタップ領域が高さ19pxしか無く指で押しづらい（text-audit の [tap] 指摘） */
    const head = src
      ? `<a class="st-rv__head" href="/products/${esc(r.h)}">
           <span class="st-rv__thumb">
             <img src="${esc(src)}" alt="" loading="lazy" width="44" height="44">
           </span>
           <span class="st-rv__pname">${esc(name)}</span>
         </a>`
      : '';
    return `<article class="st-rv__card">
      ${head}
      ${stars(r.r)}
      ${r.t ? `<h3 class="st-rv__title">${esc(r.t)}</h3>` : ''}
      <p class="st-rv__body">${esc(r.b)}</p>
      <footer class="st-rv__meta">
        <span class="st-rv__date"><i>掲載日</i>${esc(r.d)}</span>
        <span class="st-rv__name">${esc(r.n)}</span>
      </footer>
    </article>`;
  }

  /**
   * FV直下の横スライド口コミ（2026-08-13 クライアント要望）。
   *   <st-reviews-slider handle="seitaitea-hare" limit="8"></st-reviews-slider>
   *
   * 参考はサヌキヌードルのFV（自動で横に流れるレビュー）。
   * ・スクロールスナップの実体はCSS。JSは自動送りと矢印だけ
   * ・お客様が触ったら自動送りは止める（読んでいる途中に流さない）
   * ・決定事項 §3: 取れなければ枠ごと消す。エラー文は出さない
   */
  class StReviewsSlider extends HTMLElement {
    async connectedCallback() {
      const handle = this.getAttribute('handle') || '';
      const limit = Math.max(1, parseInt(this.getAttribute('limit') || '8', 10));

      const u = new URL(`${API}/v1/reviews`);
      u.searchParams.set('limit', String(limit));
      u.searchParams.set('offset', '0');
      if (handle) u.searchParams.set('handle', handle);
      const [j, s] = await Promise.all([getJSON(u.toString()), getSummary()]);
      const rows = j?.reviews || [];
      if (!rows.length) { this.remove(); return; }

      // ★と件数。handle指定ならその商品、無指定なら全商品の合算
      let avg = 0, count = 0;
      if (handle) {
        avg = s?.[handle]?.avg || 0; count = s?.[handle]?.count || 0;
      } else if (s) {
        const vals = Object.values(s);
        count = vals.reduce((a, b) => a + (b.count || 0), 0);
        avg = count ? vals.reduce((a, b) => a + (b.avg || 0) * (b.count || 0), 0) / count : 0;
      }
      const voiceUrl = handle ? `/pages/voice?product=${encodeURIComponent(handle)}` : '/pages/voice';

      this.innerHTML = `
        ${count ? `<a class="st-rvs__total" href="${esc(voiceUrl)}">${stars(Math.round(avg * 100) / 100)}<b>${avg.toFixed(2)}</b><span>（${count}件の口コミ）</span></a>` : ''}
        <div class="st-rvs__frame">
          <div class="st-rvs__track" data-track>
            ${rows.map((r) => `<div class="st-rvs__cell">${card(r)}</div>`).join('')}
          </div>
          <button type="button" class="st-rvs__nav st-rvs__nav--prev" aria-label="前の口コミへ">‹</button>
          <button type="button" class="st-rvs__nav st-rvs__nav--next" aria-label="次の口コミへ">›</button>
        </div>
        <p class="st-rvs__more"><a href="${esc(voiceUrl)}">口コミをもっと見る<span aria-hidden="true">›</span></a></p>`;

      const track = this.querySelector('[data-track]');
      const cells = [...this.querySelectorAll('.st-rvs__cell')];
      const step = () => cells[1] ? cells[1].offsetLeft - cells[0].offsetLeft : track.clientWidth;
      const go = (dir) => {
        const max = track.scrollWidth - track.clientWidth;
        let left = track.scrollLeft + dir * step();
        // 端まで来たら先頭へ巻き戻す（自動送りが右端で固まらないように）
        if (dir > 0 && track.scrollLeft >= max - 8) left = 0;
        track.scrollTo({ left: Math.max(0, Math.min(max, left)), behavior: 'smooth' });
      };
      this.querySelector('.st-rvs__nav--prev').addEventListener('click', () => { stopForever(); go(-1); });
      this.querySelector('.st-rvs__nav--next').addEventListener('click', () => { stopForever(); go(1); });

      // 自動送り。触ったら止める・画面外では回さない・reduced motionでは回さない
      let timer = null, stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const play = () => { if (!timer && !stopped) timer = setInterval(() => go(1), 5000); };
      const halt = () => { if (timer) { clearInterval(timer); timer = null; } };
      const stopForever = () => { stopped = true; halt(); };
      track.addEventListener('pointerdown', stopForever, { passive: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((es) => es.forEach((e) => (e.isIntersecting ? play() : halt())), { threshold: 0.3 }).observe(track);
      } else {
        play();
      }
      document.addEventListener('visibilitychange', () => (document.hidden ? halt() : play()));
    }
  }

  customElements.define('st-review-rating', StReviewRating);
  customElements.define('st-reviews-total', StReviewsTotal);
  customElements.define('st-reviews', StReviews);
  customElements.define('st-reviews-slider', StReviewsSlider);

  /**
   * 手書きの★バッジ（st-seimei-intro など）の件数だけを実データに差し替える。
   *
   * ★なぜカスタム要素にしなかったか（2026-09-05）★
   *   このバッジはLPのギャラリー直下にある信頼要素で、セクションごとに見た目が違う。
   *   要素ごと差し替える作りにすると、APIが落ちたとき★ごと消えてLPが寂しくなる。
   *   支給値を先にHTMLで描いておき、取れたときだけ数字を上書きする形にした。
   *   取れなければ支給値がそのまま残る（§3の「枠ごと消す」はAPI前提の枠にだけ適用する）。
   *
   *   data-st-rvcount=""          … 全商品の合計
   *   data-st-rvcount="<handle>"  … その商品だけ
   */
  async function fillCounts() {
    const nodes = document.querySelectorAll('[data-st-rvcount]');
    if (!nodes.length) return;
    const s = await getSummary();
    if (!s) return;
    nodes.forEach((el) => {
      const h = el.getAttribute('data-st-rvcount');
      const count = h
        ? (s[h] || {}).count
        : Object.values(s).reduce((a, d) => a + (d.count || 0), 0);
      if (!count) return;
      const slot = el.querySelector('[data-st-rvcount-slot]') || el;
      slot.textContent = `レビュー${count}件`;
      el.classList.remove('is-pending');  // 支給値がなくて隠していた枠をここで出す
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fillCounts);
  } else {
    fillCounts();
  }
})();
